import { fetchUtil } from "../../helpers/fetchUtil";
import AuthService from "../../services/AuthService";

export const SIGN_OUT = "SIGN_OUT";

// Action creator for sign out
export const signOut = () => (dispatch) => {
  // Reset auth state when logging out
  AuthService.resetAuthState();
  
  // Clear localStorage
  localStorage.removeItem('isAuthenticated');
  
  // Dispatch sign out action
  dispatch({ type: SIGN_OUT });
  
  return Promise.resolve();
};

export const SET_TOKEN = "SET_TOKEN";

// Login actions
export const LOGIN_REQUEST = "LOGIN_REQUEST";
export const LOGIN_FAIL = "LOGIN_FAIL";
export const LOGIN_SUCCESS = "LOGIN_SUCCESS";

// Signup actions
export const SIGNUP_REQUEST = "SIGNUP_REQUEST";
export const SIGNUP_FAIL = "SIGNUP_FAIL";
export const SIGNUP_SUCCESS = "SIGNUP_SUCCESS";

// get profile action
export const ME_REQUEST = "ME_REQUEST";
export const ME_SUCCESS = "ME_SUCCESS";
export const ME_ERROR = "ME_ERROR";

// forget password actions
export const FORGET_PASSWORD_REQUEST = "FORGET_PASSWORD_REQUEST";
export const FORGET_PASSWORD_FAIL = "FORGET_PASSWORD_FAIL";
export const FORGET_PASSWORD_SUCCESS = "FORGET_PASSWORD_SUCCESS";

export const EDIT_PROFILE_SUCCESS = "EDIT_PROFILE_SUCCESS";

export const SET_BIG_COMMERCE_URL = "SET_BIG_COMMERCE_URL"

export const login = (email, password, region = 'us') => (dispatch) => {
  console.log('Login attempt for:', email);
  dispatch({ type: LOGIN_REQUEST });
  
  // Initialize auth promise before login attempt
  AuthService.initAuthPromise();
  
  const body = JSON.stringify({
    email: email,
    password: password,
    region: region
  });
  console.log('Login request payload:', { email, region });
  
  // Skip auth check for login request to avoid circular dependency
  return fetchUtil(`/login`, {
    method: "POST",
    body,
    skipAuthCheck: true
  })
    .then(async (res) => {
      console.log('Login response:', res); // Debug the response structure
      
      // Set token in store
      dispatch({ type: SET_TOKEN, token: res.id });
      
      // We need to make sure multipassUrl and multipassUrlExpiry are properly passed to the reducer
      // Log exact structure for debugging
      console.log('Login Response Structure:', JSON.stringify(res, null, 2));
      
      // The backend response structure is:
      // {
      //   id: "token",
      //   ttl: 900,
      //   created: "2025-06-03T10:18:40.000Z",
      //   userId: 119495,
      //   multipassUrl: "https://davinci-vaporizer-us.myshopify.com/account/login/multipass/...",
      //   multipassUrlExpiry: "2025-06-03T10:33:40.000Z"
      // }
      
      dispatch({ 
        type: LOGIN_SUCCESS, 
        payload: {
          data: {
            user: { id: res.userId },
            token: res.id,
            multipassUrl: res.multipassUrl,
            multipassUrlExpiry: res.multipassUrlExpiry
          }
        },
        token: res.id
      });
      
      // Signal that authentication is ready with user data
      // This resolves the auth promise that all other API calls will wait for
      AuthService.setAuthReady({
        userId: res.userId,
        token: res.id
      });
      
      await dispatch(getMe(res.id, res.userId));
      localStorage.isAuthenticated = true;
      return Promise.resolve(res);
    })
    .catch((err) => {
      //   logger(err);
      dispatch({
        type: LOGIN_FAIL,
        payload: err,
      });
      return Promise.reject(err?.message || "login failed");
    });
};

export const getMe = (user_id, token) => (dispatch, getState) => {
  dispatch({ type: ME_REQUEST });
  
  // ENHANCED VALIDATION: Double check userId and token with multiple safeguards
  // This should completely prevent any API calls with undefined user ID
  
  // First, explicitly check for undefined or null values
  if (user_id === undefined || user_id === null || user_id === 'undefined' || user_id === 'null') {
    console.error('getMe: Explicitly invalid userId provided:', user_id);
    dispatch({ type: ME_ERROR, payload: { message: 'Invalid userId provided' } });
    return Promise.reject(new Error('Invalid userId provided to getMe'));
  }
  
  // Get userId and token either from params or from Redux state
  let resolvedUserId = user_id;
  let resolvedToken = token;
  
  // If not provided as arguments, try to get from state
  if (!resolvedUserId || !resolvedToken) {
    const state = getState();
    
    // Only use state values if they are valid
    const stateUserId = state.userAuth?.user?.id;
    const stateToken = state.userAuth?.user?.token;
    
    // Check state values are valid before using them
    if (!resolvedUserId) {
      if (AuthService.isValidUserId(stateUserId)) {
        resolvedUserId = stateUserId;
      } else {
        console.error('getMe: Invalid state userId, cannot fetch user profile');
        dispatch({ type: ME_ERROR, payload: { message: 'Invalid state userId' } });
        return Promise.reject(new Error('Invalid state userId'));
      }
    }
    
    resolvedToken = resolvedToken || stateToken;
  }
  
  // Final validation check before attempting API call
  if (!AuthService.isValidUserId(resolvedUserId)) {
    console.error('getMe: Final check - invalid userId, cannot fetch user profile:', resolvedUserId);
    dispatch({ type: ME_ERROR, payload: { message: 'Invalid userId after resolution' } });
    return Promise.reject(new Error('Invalid userId after resolution'));
  }
  
  if (!resolvedToken) {
    console.error('getMe: Missing token, cannot fetch user profile');
    dispatch({ type: ME_ERROR, payload: { message: 'Missing token' } });
    return Promise.reject(new Error('Missing token'));
  }
  
  // Build URL carefully to avoid undefined
  const url = `/${resolvedUserId}`;
  
  // Log the exact URL being used
  console.log('getMe: Making API call with URL:', url);
  
  return fetchUtil(url, {
    token: resolvedToken
  })
    .then((res) => {
      console.log('getMe: Success fetching user profile');
      dispatch({ type: ME_SUCCESS, payload: res });
      localStorage.isAuthenticated = true;
      return Promise.resolve(res);
    })
    .catch((err) => {
      console.error('getMe: Error fetching user profile:', err);
      dispatch({ type: ME_ERROR, payload: err });
      return Promise.reject(err);
    });
};

export const signup = (email, password) => (dispatch) => {
  dispatch({ type: SIGNUP_REQUEST });
  const body = JSON.stringify({
    email: email,
    password: password,
  });
  return fetchUtil(`/`, {
    method: "POST",
    body
  })
    .then((res) => {
      dispatch({ type: SIGNUP_SUCCESS, payload: res });
      return Promise.resolve(res);
    })
    .catch((err) => {
      //   logger(err);
      dispatch({
        type: SIGNUP_FAIL,
        payload: err,
      });
      return Promise.reject(err?.message || "signup failed");
    });
};

export const forgotPassword = (email, region) => (dispatch, getState) => {
  let body = JSON.stringify({
    email,
    region,
  });
  dispatch({ type: FORGET_PASSWORD_REQUEST });
  return fetchUtil(`/forgot-password`, {
    method: "POST",
    body
  })
    .then((res) => {
      dispatch({ type: FORGET_PASSWORD_SUCCESS, payload: {} });
      return Promise.resolve({});
    })
    .catch((err) => {
      dispatch({
        type: FORGET_PASSWORD_FAIL,
        payload: err,
      });
      return Promise.reject(err?.message || "Forget password failed");
    });
};

export const editProfile = (id, bodyData) => (dispatch, getState) => {
  const token = getState().userAuth.user.token;
  return fetchUtil(`/${id}`, {
    method: "PUT",
    body: JSON.stringify(bodyData),
    token
  })
    .then((res) => {
      dispatch({ type: EDIT_PROFILE_SUCCESS, payload: res });
      return Promise.resolve(res);
    })
    .catch((err) => {
      return Promise.reject(err);
    });
};

export const changePassword = (id, bodyData) => (dispatch, getState) => {
  const token = getState().userAuth.user.token;

  return fetchUtil(`/${id}`, {
    method: "PUT",
    body: JSON.stringify(bodyData),
    token
  })
    .then((res) => {
      return Promise.resolve(res);
    })
    .catch((err) => {
      return Promise.reject(err);
    });
};


export const getMultipassUrl = () => (dispatch, getState) => {
  const token = getState().userAuth.user.token;
  
  // We need to use API instead of API_URL to avoid duplicate path
  // Since /api/DaVinciUsers is already in API_URL, we need to use externalApi flag
  return fetchUtil(`/regenerateMultipass`, {
    method: "POST",
    body: JSON.stringify({}),
    token,
    // Using externalApi: false by default to use API_URL with /api/DaVinciUsers already included
  })
    .then((res) => {
      console.log('Multipass URL generated successfully:', res?.multipassUrl ? 'Success' : 'No URL');
      // Update the multipassUrl in the Redux store if needed
      return Promise.resolve(res);
    })
    .catch((err) => {
      console.error('Error generating Multipass URL:', err);
      return Promise.reject(err);
    });
};
